import {
  ListRequest,
  useRltCalculationListQuery,
} from "@/services/backend/rlt/calculations/api/calculation-api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";
import { Search } from "lucide-react";
import { Calculation } from "@/services/backend/rlt/calculations/types/calculation";
import { FilterMode } from "@/shared/lib/filter/filter";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";

export function CalculationListTable({
  request,
  onRequestChange,
  renderRowLink,
  hidePagination = false,
  hideBorder = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (calculationId: string, children: ReactNode) => ReactNode;
  hidePagination?: boolean;
  hideBorder?: boolean;
}) {
  const { data: list, isLoading, error } = useRltCalculationListQuery(request);

  const emptyList = !isLoading && !error && list!.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead className="w-full">{t("Erstellt am")}</TableHead>
              <TableHead className="w-48">{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={3} />}
            {error && <TableRtkErrorRow error={error} colSpan={3} />}
            {emptyList && (
              <TableEmptyRow
                colSpan={3}
                text={t("Keine Kalkulationen gefunden")}
              />
            )}
            {list &&
              list.items.map((calculation) => (
                <CalculationListTableRow
                  key={calculation.id}
                  calculation={calculation}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) =>
            onRequestChange({ ...request, limit, offset })
          }
          className="ml-auto"
        />
      )}
    </div>
  );
}

function CalculationListTableRow({
  calculation,
  renderRowLink,
}: {
  calculation: Calculation;
  renderRowLink: (calculationId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        {renderRowLink(
          calculation.id,
          <DocumentNumberById documentId={calculation.id} />,
        )}
      </TableCell>
      <TableCell>
        {new Date(calculation.createdAt).toLocaleDateString()}
      </TableCell>
      <TableCell className="text-right">
        {renderRowLink(
          calculation.id,
          <Button variant="outline" size="sm">
            <Search className="h-4 w-4" />
          </Button>,
        )}
      </TableCell>
    </TableRow>
  );
}

export function CalculationListTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <OrderBySelect<string>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Aktualisiert am"), value: "updatedAt" },
        ]}
      />
    </div>
  );
}

export function useDefaultRltCalculationListRequest(
  processId: string,
): ListRequest {
  return {
    process: { active: true, values: [processId] },
    owner: { active: true, mode: FilterMode.IsNull, values: [] },
    customer: { active: false, values: [] },
    immutable: { active: false, values: [] },
    orderBy: [{ field: "createdAt" }],
    limit: 50,
    offset: 0,
  };
}
